import {
  deleteDoc,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { logAuditEvent } from './auditLedgerService'
import {
  BOUND_INTENTS,
  DEFAULT_DESIGN_CANVAS,
  migrateLegacyBlocks,
  validateDesignAssetDraft,
  type BoundIntent,
  type BoundOutput,
  type BoundOutputStatus,
  type DesignAsset,
  type DesignAssetDraft,
  type DesignShape,
} from '../types/design'

export const DESIGN_ASSETS_COLLECTION = 'design_assets'

export type DesignAssetActor = { id: string; name: string }

const BOUND_OUTPUT_STATUSES: BoundOutputStatus[] = ['NONE', 'DRAFT', 'MATERIALIZED', 'STALE']

const readBoundIntent = (raw: unknown): BoundIntent | undefined =>
  typeof raw === 'string' && (BOUND_INTENTS as string[]).includes(raw) ? (raw as BoundIntent) : undefined

const readBoundStatus = (raw: unknown): BoundOutputStatus | undefined =>
  typeof raw === 'string' && (BOUND_OUTPUT_STATUSES as string[]).includes(raw) ? (raw as BoundOutputStatus) : undefined

const readBoundOutput = (raw: unknown): BoundOutput | undefined => {
  if (!raw || typeof raw !== 'object') return undefined
  const record = raw as Record<string, unknown>
  const kind = readBoundIntent(record.kind)
  if (!kind) return undefined
  return {
    kind,
    targetId: typeof record.targetId === 'string' ? record.targetId : null,
    status: readBoundStatus(record.status),
    lastMaterializedAt: (record.lastMaterializedAt as BoundOutput['lastMaterializedAt']) ?? null,
  }
}

const hydrateAsset = (id: string, data: Record<string, unknown> | undefined): DesignAsset | null => {
  if (!data) return null
  const rawShapes = Array.isArray(data.shapes) ? (data.shapes as DesignShape[]) : null
  const shapes = rawShapes ?? migrateLegacyBlocks(data.blocks)
  return {
    id,
    workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : '',
    title: typeof data.title === 'string' ? data.title : 'Untitled design',
    mode: data.mode === 'BOUND' || data.mode === 'FREE' ? data.mode : 'FREE',
    canvas: typeof data.canvas === 'object' && data.canvas ? data.canvas as DesignAsset['canvas'] : DEFAULT_DESIGN_CANVAS,
    shapes,
    templateOf: typeof data.templateOf === 'string' ? data.templateOf : null,
    createdById: typeof data.createdById === 'string' ? data.createdById : '',
    createdByName: typeof data.createdByName === 'string' ? data.createdByName : '',
    createdAt: (data.createdAt as DesignAsset['createdAt']) ?? null,
    updatedAt: (data.updatedAt as DesignAsset['updatedAt']) ?? null,
    boundIntent: readBoundIntent(data.boundIntent),
    boundVersion: typeof data.boundVersion === 'number' ? data.boundVersion : undefined,
    boundOutput: readBoundOutput(data.boundOutput),
    boundOutputStatus: readBoundStatus(data.boundOutputStatus),
  }
}

export const getDesignAsset = async (assetId: string): Promise<DesignAsset | null> => {
  const snapshot = await getDoc(doc(firestoreDb, DESIGN_ASSETS_COLLECTION, assetId))
  if (!snapshot.exists()) return null
  return hydrateAsset(snapshot.id, snapshot.data() as Record<string, unknown>)
}

export const saveDesignAsset = async (draft: DesignAssetDraft, actor: DesignAssetActor): Promise<{ created: boolean }> => {
  const errors = validateDesignAssetDraft(draft)
  if (errors.length > 0) throw new Error(errors[0])

  const assetRef = doc(firestoreDb, DESIGN_ASSETS_COLLECTION, draft.id)
  return await runTransaction(firestoreDb, async (transaction) => {
    const [current, workspace] = await Promise.all([
      transaction.get(assetRef),
      transaction.get(doc(firestoreDb, 'workspaces', draft.workspaceId)),
    ])
    const isNew = !current.exists()
    const trimmedTitle = draft.title.trim()
    const currentData = current.data() as Record<string, unknown> | undefined
    transaction.set(assetRef, {
      workspaceId: draft.workspaceId,
      title: trimmedTitle,
      mode: draft.mode,
      canvas: draft.canvas ?? DEFAULT_DESIGN_CANVAS,
      shapes: draft.shapes,
      templateOf: draft.templateOf ?? currentData?.templateOf ?? null,
      blocks: null,
      createdById: isNew ? actor.id : (currentData?.createdById as string | undefined) ?? actor.id,
      createdByName: isNew ? actor.name : (currentData?.createdByName as string | undefined) ?? actor.name,
      createdAt: isNew ? serverTimestamp() : currentData?.createdAt ?? serverTimestamp(),
      updatedAt: serverTimestamp(),
      boundIntent: draft.boundIntent ?? readBoundIntent(currentData?.boundIntent) ?? null,
      boundVersion: typeof draft.boundVersion === 'number' ? draft.boundVersion : (currentData?.boundVersion ?? null),
      boundOutput: draft.boundOutput ?? readBoundOutput(currentData?.boundOutput) ?? null,
      boundOutputStatus: draft.boundOutputStatus ?? readBoundStatus(currentData?.boundOutputStatus) ?? null,
    }, { merge: true })
    logAuditEvent(firestoreDb, transaction, workspace.data(), {
      workspaceId: draft.workspaceId,
      actorId: actor.id,
      actorName: actor.name,
      targetId: draft.id,
      nodeType: 'DESIGN_ASSET',
      targetTitle: trimmedTitle,
      action: isNew ? 'DESIGN_ASSET_CREATED' : 'DESIGN_ASSET_UPDATED',
      activityType: isNew ? 'CREATE' : 'UPDATE',
      summary: `${isNew ? 'Created' : 'Saved'} design asset: ${trimmedTitle}`,
      payload: { shapeCount: draft.shapes.length, mode: draft.mode },
    })
    return { created: isNew }
  })
}

export const stampDesignAssetBoundOutput = async (
  assetId: string,
  patch: { boundIntent: BoundIntent; boundOutput: BoundOutput; boundOutputStatus: BoundOutputStatus },
): Promise<void> => {
  const assetRef = doc(firestoreDb, DESIGN_ASSETS_COLLECTION, assetId)
  await runTransaction(firestoreDb, async (transaction) => {
    const current = await transaction.get(assetRef)
    if (!current.exists()) return
    // Do NOT bump updatedAt here — otherwise the STALE check would flag every asset immediately after materialization.
    transaction.update(assetRef, {
      boundIntent: patch.boundIntent,
      boundOutput: {
        ...patch.boundOutput,
        lastMaterializedAt: serverTimestamp(),
      },
      boundOutputStatus: patch.boundOutputStatus,
    })
  })
}

export const deleteDesignAsset = async (asset: DesignAsset, actor: DesignAssetActor): Promise<void> => {
  const assetRef = doc(firestoreDb, DESIGN_ASSETS_COLLECTION, asset.id)
  await runTransaction(firestoreDb, async (transaction) => {
    const [current, workspace] = await Promise.all([
      transaction.get(assetRef),
      transaction.get(doc(firestoreDb, 'workspaces', asset.workspaceId)),
    ])
    if (!current.exists()) return
    transaction.delete(assetRef)
    logAuditEvent(firestoreDb, transaction, workspace.data(), {
      workspaceId: asset.workspaceId,
      actorId: actor.id,
      actorName: actor.name,
      targetId: asset.id,
      nodeType: 'DESIGN_ASSET',
      targetTitle: asset.title,
      action: 'DESIGN_ASSET_DELETED',
      activityType: 'DELETE',
      summary: `Deleted design asset: ${asset.title}`,
    })
  })
  await deleteDoc(assetRef).catch(() => undefined)
}

export const hydrateDesignAsset = hydrateAsset
